/**
 * WorkoutDetail Page
 * Full page view of a workout with personalized paces/power
 */

import {
  ArrowBack,
  DirectionsBike,
  DirectionsRun,
  Edit,
  Schedule,
  Timer,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { WorkoutStep } from '@shared/types';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../services/api';
import { logger } from '../../utils/logger';
import { loadUserProfileSync } from '../../utils/userProfile';
import { LoadingAnimation } from '../common/LoadingAnimation';
import { WorkoutZoneProfile } from '../workout/WorkoutZoneProfile';
import { ScheduleWorkoutDialog } from './ScheduleWorkoutDialog';

interface WorkoutDetailData {
  id: string;
  name: string;
  type: 'easy' | 'hard';
  sportType: 'running' | 'cycling';
  minTime: number;
  load: number;
  TSS?: number;
  environment?: string;
  steps: WorkoutStep[];
  popularity?: number;
}

// Zone intensity percentages for pace/power calculation
const ZONE_INTENSITY = {
  1: { min: 75, max: 79 }, // Recovery
  2: { min: 80, max: 89 }, // Easy/Base
  3: { min: 90, max: 94 }, // Tempo
  4: { min: 95, max: 100 }, // Threshold
  5: { min: 100, max: 115 }, // VO2max
};

// Coggan power zones for cycling
const BIKE_ZONE_FTP_PERCENT = {
  1: { min: 55, max: 74 }, // Active Recovery
  2: { min: 75, max: 89 }, // Endurance
  3: { min: 90, max: 94 }, // Tempo/Sweet Spot
  4: { min: 95, max: 105 }, // Threshold
  5: { min: 106, max: 120 }, // VO2max
};

/**
 * Convert seconds to MM:SS format
 */
function formatPace(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format duration in seconds to human-readable string
 */
function formatDuration(seconds: number): string {
  const roundedSeconds = Math.round(seconds);
  if (roundedSeconds < 60) {
    return `${roundedSeconds}s`;
  }
  const mins = Math.floor(roundedSeconds / 60);
  const secs = roundedSeconds % 60;
  if (secs === 0) {
    return `${mins}min`;
  }
  return `${mins}m ${secs}s`;
}

/**
 * Calculate pace range for a zone based on threshold pace
 */
function getZonePace(zone: number, thresholdPaceSec: number): { min: string; max: string } {
  const intensity = ZONE_INTENSITY[zone as keyof typeof ZONE_INTENSITY];
  if (!intensity) return { min: '-', max: '-' };

  // Lower intensity % = slower pace (higher sec/km)
  const paceMin = (thresholdPaceSec / intensity.min) * 100;
  const paceMax = (thresholdPaceSec / intensity.max) * 100;

  return {
    min: formatPace(paceMax), // Faster pace (lower value)
    max: formatPace(paceMin), // Slower pace (higher value)
  };
}

/**
 * Calculate power range for a zone based on FTP
 */
function getZonePower(zone: number, ftp: number): { min: number; max: number } {
  const percent = BIKE_ZONE_FTP_PERCENT[zone as keyof typeof BIKE_ZONE_FTP_PERCENT];
  if (!percent) return { min: 0, max: 0 };

  return {
    min: Math.round((ftp * percent.min) / 100),
    max: Math.round((ftp * percent.max) / 100),
  };
}

interface FlattenedStep {
  zone: number;
  duration: number;
  distance?: number;
  text?: string;
  isRepeat?: boolean;
  repeatCount?: number;
  /** If true, this step is a ramp from startZone to endZone */
  isRamp?: boolean;
  /** For ramp steps: starting zone */
  rampStartZone?: number;
  /** For ramp steps: ending zone */
  rampEndZone?: number;
  /** FTP percentage for indoor cycling */
  ftpPercent?: number;
  /** For FTP ramp steps: starting FTP% */
  rampStartFtpPercent?: number;
  /** For FTP ramp steps: ending FTP% */
  rampEndFtpPercent?: number;
}

/**
 * Flatten workout steps for display (expand repeats and handle ramps)
 * Supports both zone-based (running) and FTP%-based (indoor cycling) workouts
 */
function flattenStepsForDisplay(steps: WorkoutStep[]): FlattenedStep[] {
  const result: FlattenedStep[] = [];

  for (const step of steps) {
    if (step.repeat && step.steps) {
      // Add repeat block indicator
      for (let i = 0; i < step.repeat; i++) {
        for (const nestedStep of step.steps) {
          // Handle FTP ramp steps inside repeats (indoor cycling)
          if (
            nestedStep.ramp &&
            nestedStep.ramp.startFtpPercent !== undefined &&
            nestedStep.ramp.endFtpPercent !== undefined
          ) {
            result.push({
              zone: 0,
              duration: nestedStep.duration || 0,
              distance: nestedStep.distance,
              text: nestedStep.text,
              isRepeat: true,
              repeatCount: step.repeat,
              isRamp: true,
              ftpPercent: nestedStep.ramp.startFtpPercent,
              rampStartFtpPercent: nestedStep.ramp.startFtpPercent,
              rampEndFtpPercent: nestedStep.ramp.endFtpPercent,
            });
          }
          // Handle FTP percentage steps inside repeats (indoor cycling)
          else if (nestedStep.ftpPercent !== undefined) {
            const ftpValue =
              typeof nestedStep.ftpPercent === 'number'
                ? nestedStep.ftpPercent
                : (nestedStep.ftpPercent.min + nestedStep.ftpPercent.max) / 2;
            result.push({
              zone: 0,
              duration: nestedStep.duration || 0,
              distance: nestedStep.distance,
              text: nestedStep.text,
              isRepeat: true,
              repeatCount: step.repeat,
              ftpPercent: ftpValue,
            });
          }
          // Handle zone ramp steps inside repeats
          else if (
            nestedStep.ramp &&
            nestedStep.ramp.startZone !== undefined &&
            nestedStep.ramp.endZone !== undefined
          ) {
            result.push({
              zone: nestedStep.ramp.startZone,
              duration: nestedStep.duration || 0,
              distance: nestedStep.distance,
              text: nestedStep.text,
              isRepeat: true,
              repeatCount: step.repeat,
              isRamp: true,
              rampStartZone: nestedStep.ramp.startZone,
              rampEndZone: nestedStep.ramp.endZone,
            });
          } else if (nestedStep.zone) {
            result.push({
              zone: nestedStep.zone,
              duration: nestedStep.duration || 0,
              distance: nestedStep.distance,
              text: nestedStep.text,
              isRepeat: true,
              repeatCount: step.repeat,
            });
          }
        }
      }
    }
    // Handle FTP ramp steps (indoor cycling)
    else if (
      step.ramp &&
      step.ramp.startFtpPercent !== undefined &&
      step.ramp.endFtpPercent !== undefined
    ) {
      result.push({
        zone: 0,
        duration: step.duration || 0,
        distance: step.distance,
        text: step.text,
        isRamp: true,
        ftpPercent: step.ramp.startFtpPercent,
        rampStartFtpPercent: step.ramp.startFtpPercent,
        rampEndFtpPercent: step.ramp.endFtpPercent,
      });
    }
    // Handle FTP percentage steps (indoor cycling)
    else if (step.ftpPercent !== undefined) {
      const ftpValue =
        typeof step.ftpPercent === 'number'
          ? step.ftpPercent
          : (step.ftpPercent.min + step.ftpPercent.max) / 2;
      result.push({
        zone: 0,
        duration: step.duration || 0,
        distance: step.distance,
        text: step.text,
        ftpPercent: ftpValue,
      });
    }
    // Handle zone ramp steps
    else if (step.ramp && step.ramp.startZone !== undefined && step.ramp.endZone !== undefined) {
      result.push({
        zone: step.ramp.startZone,
        duration: step.duration || 0,
        distance: step.distance,
        text: step.text,
        isRamp: true,
        rampStartZone: step.ramp.startZone,
        rampEndZone: step.ramp.endZone,
      });
    } else if (step.zone) {
      result.push({
        zone: step.zone,
        duration: step.duration || 0,
        distance: step.distance,
        text: step.text,
      });
    }
  }

  return result;
}

export const WorkoutDetail: React.FC = () => {
  const { workoutId } = useParams<{ workoutId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State
  const [workout, setWorkout] = useState<WorkoutDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [hoveredStepIndex, setHoveredStepIndex] = useState<number | null>(null);

  // Personalization state
  const [thresholdPace, setThresholdPace] = useState<number>(240); // Default 4:00/km
  const [ftp, setFtp] = useState<number>(200); // Default 200W
  const [isEditingThreshold, setIsEditingThreshold] = useState(false);
  const [isEditingFtp, setIsEditingFtp] = useState(false);

  // Load user profile for personalization
  useEffect(() => {
    const profile = loadUserProfileSync();
    if (profile) {
      if (profile.thresholdPaceSec) {
        setThresholdPace(profile.thresholdPaceSec);
      }
      if (profile.FTP) {
        setFtp(profile.FTP);
      }
    }
  }, []);

  // Load workout data
  useEffect(() => {
    const loadWorkout = async () => {
      if (!workoutId) {
        setError('No workout ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Try to load as a bike workout first
        const bikeResponse = await apiClient.getBikeWorkout(workoutId);
        if (bikeResponse.success && bikeResponse.data) {
          setWorkout({
            id: bikeResponse.data.id,
            name: bikeResponse.data.name,
            type: bikeResponse.data.type,
            sportType: bikeResponse.data.sportType,
            minTime: bikeResponse.data.minTime,
            load: bikeResponse.data.load,
            TSS: bikeResponse.data.TSS,
            environment: undefined, // Bike workouts handle indoor/outdoor differently
            steps: bikeResponse.data.steps,
            popularity: bikeResponse.data.popularity,
          } as WorkoutDetailData);
          return;
        }

        // Fall back to workout types (running)
        const response = await apiClient.getWorkoutTypesFull();
        if (response.success && response.data) {
          const found = response.data.find(w => w.id === workoutId);
          if (found) {
            setWorkout(found as WorkoutDetailData);
          } else {
            setError('Workout not found');
          }
        } else {
          throw new Error(response.message || 'Failed to load workout');
        }
      } catch (err: any) {
        logger.error('Failed to load workout', err);
        setError(err.message || 'Failed to load workout');
      } finally {
        setLoading(false);
      }
    };

    loadWorkout();
  }, [workoutId]);

  // Flatten steps for display
  const flatSteps = useMemo(() => {
    if (!workout) return [];
    return flattenStepsForDisplay(workout.steps);
  }, [workout]);

  // Calculate total duration
  const totalDuration = useMemo(() => {
    return flatSteps.reduce((sum, step) => sum + step.duration, 0);
  }, [flatSteps]);

  if (loading) {
    return <LoadingAnimation message="Loading workout details..." />;
  }

  if (error || !workout) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton onClick={() => navigate('/workout-library')} aria-label="Go back">
            <ArrowBack />
          </IconButton>
          <Typography color="error">{error || 'Workout not found'}</Typography>
        </Box>
      </Container>
    );
  }

  const isRunning = workout.sportType === 'running';
  const isIndoorCycling = workout.sportType === 'cycling' && workout.environment === 'indoor';
  const SportIcon = isRunning ? DirectionsRun : DirectionsBike;

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton
          onClick={() => navigate('/workout-library')}
          aria-label="Go back to workout library"
          size="small"
        >
          <ArrowBack />
        </IconButton>
        <SportIcon color={isRunning ? 'primary' : 'secondary'} />
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {workout.name}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Schedule />}
          onClick={() => setScheduleDialogOpen(true)}
          aria-label="Schedule this workout"
        >
          Schedule
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Workout Info */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Zone Profile */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Workout Profile
              </Typography>
              <WorkoutZoneProfile
                steps={workout.steps}
                height={120}
                showLabels
                sportType={workout.sportType}
                environment={workout.environment}
                onHoverStep={setHoveredStepIndex}
                hoveredStepIndex={hoveredStepIndex}
                ftp={ftp}
              />
            </CardContent>
          </Card>

          {/* Workout Details */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip
                  icon={<SportIcon sx={{ fontSize: 18 }} />}
                  label={isRunning ? 'Running' : 'Cycling'}
                  color={isRunning ? 'primary' : 'secondary'}
                />
                <Chip
                  label={workout.type === 'hard' ? 'Hard' : 'Easy'}
                  sx={{
                    backgroundColor:
                      workout.type === 'hard'
                        ? theme.palette.error.light
                        : theme.palette.success.light,
                    color:
                      workout.type === 'hard'
                        ? theme.palette.error.contrastText
                        : theme.palette.success.contrastText,
                  }}
                />
                {workout.environment && (
                  <Chip
                    label={workout.environment === 'indoor' ? 'Indoor' : 'Outdoor'}
                    variant="outlined"
                  />
                )}
                <Chip
                  icon={<Timer sx={{ fontSize: 18 }} />}
                  label={`${workout.minTime} min`}
                  variant="outlined"
                />
                {workout.TSS && <Chip label={`TSS: ${workout.TSS}`} variant="outlined" />}
                {workout.load > 0 && !workout.TSS && (
                  <Chip label={`Load: ${workout.load}`} variant="outlined" />
                )}
                {workout.popularity !== undefined && workout.popularity > 0 && (
                  <Chip
                    label={`🔥 ${workout.popularity} scheduled`}
                    variant="outlined"
                    sx={{ borderColor: 'warning.main', color: 'warning.main' }}
                  />
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Steps Table */}
              <Typography variant="h6" gutterBottom>
                Workout Steps ({formatDuration(totalDuration)} total)
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Step</TableCell>
                      <TableCell>{isIndoorCycling ? 'FTP %' : 'Zone'}</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>{isRunning ? 'Pace Range' : 'Power'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {flatSteps.map((step, index) => {
                      const paceRange = isRunning ? getZonePace(step.zone, thresholdPace) : null;
                      const powerRange =
                        !isRunning && !step.ftpPercent ? getZonePower(step.zone, ftp) : null;

                      // Calculate power for FTP-based steps
                      const ftpPower = step.ftpPercent
                        ? Math.round((step.ftpPercent / 100) * ftp)
                        : null;
                      const ftpRampEndPower = step.rampEndFtpPercent
                        ? Math.round((step.rampEndFtpPercent / 100) * ftp)
                        : null;

                      const isRowHovered = hoveredStepIndex === index;

                      // Determine if this is a high-intensity step (zone 4-5)
                      const isHighIntensity = (step.zone || 2) >= 4;

                      return (
                        <TableRow
                          key={index}
                          onMouseEnter={() => setHoveredStepIndex(index)}
                          onMouseLeave={() => setHoveredStepIndex(null)}
                          sx={{
                            backgroundColor: isRowHovered
                              ? theme.palette.action.selected
                              : isHighIntensity
                                ? theme.palette.action.hover
                                : undefined,
                            transition: 'background-color 0.2s ease',
                            cursor: 'pointer',
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2">
                              {step.text || (step.zone ? `Zone ${step.zone}` : '-')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {/* FTP ramp step (indoor cycling) */}
                            {step.isRamp &&
                            step.rampStartFtpPercent !== undefined &&
                            step.rampEndFtpPercent !== undefined ? (
                              <Chip
                                label={`${step.rampStartFtpPercent}% → ${step.rampEndFtpPercent}%`}
                                size="small"
                                sx={{
                                  background: `linear-gradient(90deg, ${getFtpColor(step.rampStartFtpPercent)} 0%, ${getFtpColor(step.rampEndFtpPercent)} 100%)`,
                                  color: '#fff',
                                  fontWeight: 600,
                                }}
                              />
                            ) : /* FTP percentage step (indoor cycling) */
                            step.ftpPercent !== undefined ? (
                              <Chip
                                label={`${Math.round(step.ftpPercent)}%`}
                                size="small"
                                sx={{
                                  backgroundColor: getFtpColor(step.ftpPercent),
                                  color: '#fff',
                                  fontWeight: 600,
                                }}
                              />
                            ) : /* Zone ramp step */
                            step.isRamp && step.rampStartZone && step.rampEndZone ? (
                              <Chip
                                label={`Z${step.rampStartZone} → Z${step.rampEndZone}`}
                                size="small"
                                sx={{
                                  background: `linear-gradient(90deg, ${getZoneColor(step.rampStartZone)} 0%, ${getZoneColor(step.rampEndZone)} 100%)`,
                                  color: '#fff',
                                  fontWeight: 600,
                                }}
                              />
                            ) : (
                              /* Regular zone step */
                              <Chip
                                label={`Z${step.zone}`}
                                size="small"
                                sx={{
                                  backgroundColor: getZoneColor(step.zone),
                                  color: '#fff',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {step.distance ? `${step.distance}m` : formatDuration(step.duration)}
                          </TableCell>
                          <TableCell>
                            {isRunning && paceRange
                              ? `${paceRange.min} - ${paceRange.max}/km`
                              : step.isRamp && ftpPower && ftpRampEndPower
                                ? `${ftpPower}W → ${ftpRampEndPower}W`
                                : ftpPower
                                  ? `${ftpPower}W`
                                  : powerRange
                                    ? `${powerRange.min} - ${powerRange.max}W`
                                    : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Personalization */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ position: isMobile ? 'static' : 'sticky', top: 80 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Your Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Paces and power zones are calculated based on your threshold values.
              </Typography>

              {/* Threshold Pace (Running) */}
              {isRunning && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2">Threshold Pace</Typography>
                    <Tooltip title="Edit threshold pace">
                      <IconButton
                        size="small"
                        onClick={() => setIsEditingThreshold(!isEditingThreshold)}
                        aria-label="Edit threshold pace"
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {isEditingThreshold ? (
                    <TextField
                      value={thresholdPace}
                      onChange={e => setThresholdPace(parseInt(e.target.value) || 240)}
                      type="number"
                      size="small"
                      fullWidth
                      InputProps={{
                        endAdornment: <InputAdornment position="end">sec/km</InputAdornment>,
                      }}
                      aria-label="Threshold pace in seconds per kilometer"
                      onBlur={() => setIsEditingThreshold(false)}
                    />
                  ) : (
                    <Typography variant="h5" color="primary">
                      {formatPace(thresholdPace)}/km
                    </Typography>
                  )}
                </Box>
              )}

              {/* FTP (Cycling) */}
              {!isRunning && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2">FTP (Functional Threshold Power)</Typography>
                    <Tooltip title="Edit FTP">
                      <IconButton
                        size="small"
                        onClick={() => setIsEditingFtp(!isEditingFtp)}
                        aria-label="Edit FTP"
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {isEditingFtp ? (
                    <TextField
                      value={ftp}
                      onChange={e => setFtp(parseInt(e.target.value) || 200)}
                      type="number"
                      size="small"
                      fullWidth
                      InputProps={{
                        endAdornment: <InputAdornment position="end">W</InputAdornment>,
                      }}
                      aria-label="FTP in watts"
                      onBlur={() => setIsEditingFtp(false)}
                    />
                  ) : (
                    <Typography variant="h5" color="secondary">
                      {ftp}W
                    </Typography>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Zone Reference */}
              <Typography variant="subtitle2" gutterBottom>
                Zone Reference
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[1, 2, 3, 4, 5].map(zone => {
                  const pace = isRunning ? getZonePace(zone, thresholdPace) : null;
                  const power = !isRunning ? getZonePower(zone, ftp) : null;

                  return (
                    <Box
                      key={zone}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: theme.palette.action.hover,
                      }}
                    >
                      <Chip
                        label={`Z${zone}`}
                        size="small"
                        sx={{
                          backgroundColor: getZoneColor(zone),
                          color: '#fff',
                          fontWeight: 600,
                          minWidth: 40,
                        }}
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {getZoneName(zone)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isRunning && pace
                          ? `${pace.min} - ${pace.max}`
                          : power
                            ? `${power.min}-${power.max}W`
                            : '-'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Schedule Dialog */}
      {workout && (
        <ScheduleWorkoutDialog
          open={scheduleDialogOpen}
          onClose={() => setScheduleDialogOpen(false)}
          workoutToSchedule={{
            id: workout.id,
            name: workout.name,
            minTime: workout.minTime,
            load: workout.load,
            type: workout.type,
            sportType: workout.sportType,
            environment: workout.environment as 'indoor' | 'outdoor' | undefined,
          }}
          onSuccess={() => {
            setScheduleDialogOpen(false);
            navigate('/');
          }}
        />
      )}
    </Container>
  );
};

// Helper function to get zone color
function getZoneColor(zone: number): string {
  const colors: Record<number, string> = {
    1: '#4CAF50', // Green - Recovery
    2: '#8BC34A', // Light Green - Easy/Base
    3: '#FFC107', // Amber - Tempo
    4: '#FF9800', // Orange - Threshold
    5: '#F44336', // Red - VO2max
  };
  return colors[zone] || '#9E9E9E';
}

// Helper function to get color based on FTP percentage (Coggan power zones)
function getFtpColor(ftpPercent: number): string {
  if (ftpPercent < 56) return '#9E9E9E'; // Recovery - grey
  if (ftpPercent < 76) return '#4CAF50'; // Active Recovery/Endurance - green
  if (ftpPercent < 90) return '#8BC34A'; // Endurance - light green
  if (ftpPercent < 95) return '#FFC107'; // Tempo/Sweet Spot - amber
  if (ftpPercent < 106) return '#FF9800'; // Threshold - orange
  return '#F44336'; // VO2max+ - red
}

// Helper function to get zone name
function getZoneName(zone: number): string {
  const names: Record<number, string> = {
    1: 'Recovery',
    2: 'Easy/Base',
    3: 'Tempo',
    4: 'Threshold',
    5: 'VO2max',
  };
  return names[zone] || 'Unknown';
}

export default WorkoutDetail;
